import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FiX } from 'react-icons/fi';
import { getSiteBanner } from '@/api/content.api';

const DISMISS_KEY = 'maktech_banner_dismissed';

function readDismissedId(): string | null {
  try {
    return localStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

function writeDismissedId(id: string): void {
  try {
    localStorage.setItem(DISMISS_KEY, id);
  } catch {
    /* private mode */
  }
}

/**
 * Site-wide promo strip above the public header.
 * Text comes from Admin → Announcements → Site-wide banner.
 * Close dismisses this banner id until admin publishes again (new id).
 */
export function SiteWideBanner() {
  const qc = useQueryClient();
  const { data: banner } = useQuery({
    queryKey: ['site-banner'],
    queryFn: getSiteBanner,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  const [dismissedId, setDismissedId] = useState<string | null>(() => readDismissedId());

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === DISMISS_KEY) setDismissedId(e.newValue);
      if (e.key?.startsWith('maktech_admin_demo')) {
        void qc.invalidateQueries({ queryKey: ['site-banner'] });
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [qc]);

  const dismiss = useCallback(() => {
    if (!banner) return;
    writeDismissedId(banner.id);
    setDismissedId(banner.id);
  }, [banner]);

  if (!banner || dismissedId === banner.id) return null;

  return (
    <div
      className="relative z-[70] bg-[linear-gradient(135deg,var(--ember),var(--cobalt))] text-white"
      data-testid="site-wide-banner"
      role="region"
      aria-label="Site announcement"
    >
      <div className="container relative mx-auto px-5 py-2.5 lg:px-6">
        <p className="px-9 text-center text-[13px] font-semibold leading-snug sm:text-[13.5px]">{banner.text}</p>
        <button
          type="button"
          className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-white/90 transition hover:bg-white/15 hover:text-white lg:right-4"
          aria-label="Dismiss banner"
          title="Close"
          data-testid="site-wide-banner-close"
          onClick={dismiss}
        >
          <FiX className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
